import { useState, useEffect, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import { addCommas, cleanSpecialNaming, isInvalidDate, parseApiError } from "../HelperFx/common";
import { companyDefault, base64backendtoken, DeleteBCLines } from "../HelperFx/datalinks";
import ErrorMessage from "./ErrorPopup";
import SuccessMessage from "./SuccessPopup";
import ConfirmationDialog from "./Confirmation";
import axios from "axios";
import SearchPopup from "./SearchPopup";

import "./Datatable.css";
const PopupSelectCell = ({
  value,
  onChange,
  onBlur,
  disabled,
  fetchOptions,
  minChars = 1,
  placeholder = "Click to search...",
  rowId,
  selectOnly = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const inputRef = useRef(null);
  const fetchRef = useRef(fetchOptions);
  fetchRef.current = fetchOptions;

  useEffect(() => {
    if (value) {
      setInputValue(value);
    } else {
      setInputValue("");
    }
  }, [value]);

  const openPopup = () => {
    if (!disabled) {
      setIsPopupOpen(true);
    }
  };

  const handleSelect = (option) => {
    setInputValue(option.label || option.value || "");
    onChange(option.value);
    setIsPopupOpen(false);
    if (onBlur) {
      onBlur(selectOnly ? option : option.value);
    }
  };

  const handleInputChange = (e) => {
    if (selectOnly) return;
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const stableFetch = useCallback(
    (searchTerm) => fetchRef.current(searchTerm, rowId),
    [rowId],
  );

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onClick={openPopup}
        onFocus={openPopup}
        disabled={disabled}
        placeholder={placeholder}
        className={`table-input-cust ${disabled ? "table-input-disabled" : "table-input-editable"}`}
        title={disabled ? inputValue : placeholder}
        readOnly={selectOnly}
      />

      <SearchPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onSelect={handleSelect}
        fetchOptions={stableFetch}
        minChars={minChars}
        placeholder="Type to search..."
        currentValue={selectOnly ? "" : inputValue}
        anchorRef={inputRef}
      />
    </div>
  );
};

const ExpandedComponent = ({ data }) => (
  <pre>{JSON.stringify(data, null, 2)}</pre>
);

const EditableDatable = ({
  columns,
  actions = [],
  initialData,
  pageOnBlur,
  universalDisabled = false,
  linesApi,
  converteddocType,
  AllowDelete,
  onDataRefresh,
  // hideActions = false,
  LineDescription = "",
  SecondaryDescription = "",
  selectedLine = {},
  selectedSystemIds = [],
  selectedRowsinformation = [],
  secondaryLines = false,
  authToken = base64backendtoken,
  useLinesApiForDeleteAll = false,
  allowEmptyRows = true,
}) => {
  const [fetchedData, setData] = useState(initialData || []);
  const [confirmConfiguration, setConfirmConfiguration] = useState({});
  const [popupMsg, setPopupMsg] = useState("");
  // SelectedRows gets the systemIds of the selected rows
  const [selectedRows, setSelectedRows] = useState(selectedSystemIds || []);
  const [ErrorMsg, setErrorMessage] = useState("");
  const [confirmedOpen, setConfirmedOpen] = useState(false);
  const [issuccedOpen, setSucceededOpen] = useState(false);
  const [isErrOpen, setErrorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Selects first line data state
  const [firstSelectedRow, setFirstSelectedRow] = useState(selectedLine || {});
  // Contains all the selected line data
  const [selectedRowsData, setSelectedRowsData] = useState(
    selectedRowsinformation || [],
  ); // This state is ti manage all selected rows data

  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: 600,
        backgroundColor: "#f8fafc",
        color: "#334155",
      },
    },
    headRow: {
      style: {
        borderBottomWidth: "1px",
        borderBottomColor: "#e2e8f0",
        borderTopWidth: "1px",
        borderTopColor: "#e2e8f0",
        margin: "0px",
        padding: "0px",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        color: "#334155",
        "&:not(:last-of-type)": {
          borderBottomWidth: "1px",
          borderBottomColor: "#e2e8f0",
        },
        "&:hover": {
          backgroundColor: "#f8fafc",
        },
        margin: "0px",
        minHeight: "32px",
      },
    },
    cells: {
      style: {
        paddingTop: "0px",
        paddingBottom: "0px",
        paddingLeft: "0px",
        paddingRight: "0px",
      },
    },
  };

  const createEmptyRow = (idSuffix) => ({
    systemId: `empty-${idSuffix}-${Date.now()}`,
  });

  // Setting the reloading functionality of the lines.
  const refreshData = useCallback(() => {
    if (onDataRefresh) {
      // If parent provided refresh function, use it
      setIsLoading(true);
      onDataRefresh().finally(() => {
        setIsLoading(false);
      });
    } else {
      // Otherwise, reload the page
      window.location.reload();
    }
  }, [onDataRefresh]);

  const DeleteAllLines = async () => {
    try {
      setIsLoading(true);
      if (useLinesApiForDeleteAll && linesApi) {
        const ids = fetchedData
          .map((row) => row.systemSerialId || row.systemId || row.id)
          .filter(
            (id) => id && !String(id).toLowerCase().startsWith("empty"),
          );
        for (const sysId of ids) {
          await axios.delete(`${linesApi}(${sysId})?${companyDefault}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authToken}`,
            },
          });
        }
        setPopupMsg("All lines deleted successfully.");
        setSucceededOpen(true);
        setTimeout(() => {
          refreshData();
        }, 800);
        return;
      }

      let uniquedata = {};
      if (secondaryLines) {
        uniquedata = {
          docNo: `${fetchedData[0].documentNo}`,
          lineNo: `${fetchedData[0].lineNo}`,
        };
      } else {
        uniquedata = {
          docNo: `${fetchedData[0].documentNo}`,
          docType: `${converteddocType}`,
        };
      }
      const response = await axios.post(DeleteBCLines, uniquedata, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authToken}`,
        },
      });
      setPopupMsg(
        typeof response.data === "object"
          ? JSON.stringify(response.data.value, null, 2)
          : response.data || "Request sent successfully.",
      );
      setSucceededOpen(true);
    } catch (err) {
      setIsLoading(false);
      const backendMessage = parseApiError(err);
      setErrorMessage(backendMessage);
      setErrorOpen(true);
    }
  };

  const DeleteSelectedLines = async () => {
    if (selectedRows.length === 0) {
      setPopupMsg("No rows selected for deletion.");
      setSucceededOpen(true);
      return;
    }
    try {
      setIsLoading(true);
      for (const sysId of selectedRows) {
        await axios.delete(`${linesApi}(${sysId})?${companyDefault}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authToken}`,
          },
        });
      }
      setPopupMsg("Selected lines deleted successfully.");
      setSucceededOpen(true);
      setTimeout(() => {
        refreshData();
      }, 800);
    } catch (err) {
      setIsLoading(false);
      const backendMessage = parseApiError(err);
      setErrorMessage(
        backendMessage || "An error occurred while deleting the lines.",
      );
      setErrorOpen(true);
    }
  };

  useEffect(() => {
    const baseData = Array.isArray(initialData)
      ? initialData
      : initialData
        ? [initialData]
        : [];
    const filteredData = baseData.filter((row) => Object.keys(row).length > 1);

    setData((prev) => {
      if (universalDisabled || !allowEmptyRows) return filteredData;

      const prevEmpty = (Array.isArray(prev) ? prev : []).filter(
        (row) =>
          String(row.systemId || "").startsWith("empty") && !row.systemSerialId,
      );
      const empties =
        prevEmpty.length >= 2
          ? prevEmpty.slice(0, 2)
          : [
              ...prevEmpty,
              ...Array.from({ length: 2 - prevEmpty.length }, (_, i) =>
                createEmptyRow(prevEmpty.length + i + 1),
              ),
            ];
      return [...filteredData, ...empties];
    });
  }, [initialData, universalDisabled, allowEmptyRows]);

  const handleSelectedRowsChange = ({ selectedRows }) => {
    const validSelectedRows = selectedRows.filter((row) => {
      if (!row.systemId) return false;
      if (row.systemId.toLowerCase().startsWith("empty") && !row.systemSerialId)
        return false;
      return true;
    });

    const systemIds = validSelectedRows.map((row) => {
      return row.systemId.toLowerCase().startsWith("empty")
        ? row.systemSerialId
        : row.systemId;
    });

    // This will fix the issue of having both the selected ids and the row data (All the rows)
    setSelectedRows(systemIds);
    setSelectedRowsData(validSelectedRows);

    const firstValid =
      selectedRows.filter((row) => {
        if (!row.systemId) return false;
        if (
          row.systemId.toLowerCase().startsWith("empty") &&
          !row.systemSerialId
        )
          return false;
        return true;
      })[0] || null;
    setFirstSelectedRow(firstValid);
  };

  const handleInputChange = (id, field, value) => {
    const updated = fetchedData.map((row) =>
      row.systemId === id ? { ...row, [field]: value } : row,
    );
    setData(updated);
  };

  const handleInputBlur = async (id, field, value) => {
    const isOption = value && typeof value === "object";
    const fieldValue = isOption ? value.value : value;

    if (
      id.startsWith("empty") &&
      (fieldValue === undefined || fieldValue === null || fieldValue === "")
    )
      return;

    if (!isOption) {
      if (id.startsWith("empty")) {
        const temprow = initialData.find((r) => r.systemId === id);
        if (temprow && temprow[field] === fieldValue) return;
      }

      if (!id.startsWith("empty")) {
        const temprow = initialData.find((r) => r.systemId === id);
        if (temprow && temprow[field] === fieldValue) return;
      }
    }

    const updated = fetchedData.map((row) => {
      if (row.systemId !== id) return row;
      if (isOption) {
        return {
          ...row,
          [field]: fieldValue,
          description: value.description ?? row.description,
          uom: value.baseUOM ?? value.uom ?? row.uom,
          selectedItem: value,
        };
      }
      return { ...row, [field]: fieldValue };
    });

    if (typeof pageOnBlur === "function") {
      try {
        await pageOnBlur(id, field, isOption ? value : fieldValue, updated);
        const next = [...updated];
        if (!universalDisabled && allowEmptyRows) {
          const emptyCount = next.filter(
            (row) =>
              String(row.systemId || "").startsWith("empty") &&
              !row.systemSerialId,
          ).length;
          for (let i = emptyCount; i < 2; i += 1) {
            next.push(createEmptyRow(i + 1));
          }
        }
        setData(next);
        if (ErrorMsg) {
          setErrorMessage("");
          setErrorOpen(false);
        }
      } catch (error) {
        const errorMessage = parseApiError(error);
        setErrorMessage(errorMessage);
        setErrorOpen(true);
      }
    }
  };

  // Memoized fetch function for async selects with "contains" search only
  const getFetchFunction = useCallback((row, col) => {
    return async (searchTerm) => {
      // Check if fetchOptions is provided
      if (!col.fetchOptions) {
        console.warn("No fetchOptions provided for popupSelect column:");
        return [];
      }

      return col.fetchOptions(searchTerm, row);
    };
  }, []);

  const fcolumn = {
    name: "",
    selector: (row) => row.selected,
    width: "20px",
    sortable: false,
  };

  const enhancedColumns = columns.map((col) => ({
    name: col.label,
    sortable: col.sortable,
    minWidth: col.width,
    wrap: col.wrap || false,
    grow: col.grow,
    cell: (row) => {
      const rawValue = row[col.name];
      const value =
        col.format === "amount"
          ? addCommas(rawValue)
          : typeof rawValue === "string"
            ? cleanSpecialNaming(rawValue)
            : rawValue ?? "";
      const isDisabled = universalDisabled || col.disabled;

      switch (col.type) {
        case "checkbox":
          return (
            <span className="table-input-cust">
              <input
                type="checkbox"
                checked={!!value}
                tabIndex={col.tabIndex}
                onChange={(e) =>
                  handleInputChange(row.systemId, col.name, e.target.checked)
                }
                onBlur={(e) =>
                  handleInputBlur(row.systemId, col.name, e.target.checked)
                }
                disabled={isDisabled}
              />
            </span>
          );

        case "select":
          if (col.options && col.options.length > 100) {
            console.warn(
              `Column ${col.name} has ${col.options.length} options. Consider using asyncSelect type for better performance.`,
            );
          }
          return (
            <select
              value={value}
              tabIndex={col.tabIndex}
              className={`table-input-cust ${isDisabled ? "table-input-disabled" : "table-input-editable"}`}
              onChange={(e) =>
                handleInputChange(row.systemId, col.name, e.target.value)
              }
              onBlur={(e) =>
                handleInputBlur(row.systemId, col.name, e.target.value)
              }
              disabled={isDisabled}
            >
              <option value=""></option>
              {col.options?.map((opt, idx) => (
                <option key={idx} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );

        case "popupSelect": // New case for popup selection
          return (
            <PopupSelectCell
              value={value}
              onChange={(newValue) =>
                handleInputChange(row.systemId, col.name, newValue)
              }
              onBlur={(newValue) =>
                handleInputBlur(row.systemId, col.name, newValue)
              }
              disabled={isDisabled}
              fetchOptions={getFetchFunction(row, col)}
              minChars={col.minChars ?? 1}
              placeholder={col.placeholder || "Click to search..."}
              rowId={row.systemId}
              selectOnly={!!col.selectOnly}
            />
          );

        case "date": {
          const safeDate = !value || isInvalidDate(value) ? "" : value;
          return (
            <input
              type="date"
              value={safeDate}
              tabIndex={col.tabIndex}
              className={`table-input-cust ${isDisabled ? "table-input-disabled" : "table-input-editable"}`}
              onChange={(e) =>
                handleInputChange(row.systemId, col.name, e.target.value)
              }
              onBlur={(e) =>
                handleInputBlur(row.systemId, col.name, e.target.value)
              }
              onFocus={(e) => {
                if (!e.target.value || isInvalidDate(e.target.value)) {
                  const today = new Date();
                  const todayStr = today.toISOString().split("T")[0];
                  handleInputChange(row.systemId, col.name, todayStr);
                }
              }}
              disabled={isDisabled}
            />
          );
        }
        case "decimal":
          if (col.format === "amount") {
            return (
              <input
                type="text"
                value={value}
                title={value}
                tabIndex={col.tabIndex}
                className={`table-input-cust ${isDisabled ? "table-input-disabled" : "table-input-editable"}`}
                disabled
                readOnly
              />
            );
          }
          return (
            <input
              type="number"
              value={rawValue ?? ""}
              tabIndex={col.tabIndex}
              className={`table-input-cust ${isDisabled ? "table-input-disabled" : "table-input-editable"}`}
              onChange={(e) => {
                const floatValue = parseFloat(e.target.value) || "";
                handleInputChange(row.systemId, col.name, floatValue);
              }}
              onBlur={(e) => {
                const floatValue = parseFloat(e.target.value) || "";
                handleInputBlur(row.systemId, col.name, floatValue);
              }}
              disabled={isDisabled}
            />
          );
        case "integer":
          return (
            <input
              type="number"
              value={value}
              tabIndex={col.tabIndex}
              className={`table-input-cust ${isDisabled ? "table-input-disabled" : "table-input-editable"}`}
              onChange={(e) => {
                const intValue =
                  e.target.value === ""
                    ? 0
                    : parseInt(e.target.value, 10) || "";
                handleInputChange(row.systemId, col.name, intValue);
              }}
              onBlur={(e) => {
                const intValue =
                  e.target.value === ""
                    ? 0
                    : parseInt(e.target.value, 10) || "";
                handleInputBlur(row.systemId, col.name, intValue);
              }}
              disabled={isDisabled}
            />
          );
        case "customclickable":
          return (
            <span
              className="table-input-cust"
              style={{
                textDecoration: "underline",
                color: "blue",
                cursor: "pointer",
              }}
              onClick={() => col.onClick}
            >
              {value}
            </span>
          );
        case "number":
          if (col.format === "amount") {
            return (
              <input
                type="text"
                value={value}
                title={value}
                tabIndex={col.tabIndex}
                className={`table-input-cust ${isDisabled ? "table-input-disabled" : "table-input-editable"}`}
                disabled
                readOnly
              />
            );
          }
        // falls through
        case "time":
        case "datetime-local":
        case "text":
        default:
          return (
            <input
              type={col.type}
              value={value}
              title={value}
              tabIndex={col.tabIndex}
              className={`table-input-cust ${isDisabled ? "table-input-disabled" : "table-input-editable"}`}
              onChange={(e) =>
                handleInputChange(row.systemId, col.name, e.target.value)
              }
              onBlur={(e) =>
                handleInputBlur(row.systemId, col.name, e.target.value)
              }
              disabled={isDisabled}
            />
          );
      }
    },
  }));

  const finalColumns = [fcolumn, ...enhancedColumns];

  return (
    <div style={{ minHeight: "40px", marginTop: "10px" }}>
      {/* {!hideActions && ( */}
      <h6
        style={{
          display: "flex",
          flexWrap: "wrap",
          backgroundColor: "white",
          padding: "15px",
          // marginBottom: "10px",
          // marginTop: "10px",
          paddingBottom: "5px",
        }}
      >
        {LineDescription}
      </h6>
      <p
        style={{
          display: "flex",
          flexWrap: "wrap",
          backgroundColor: "white",
          padding: "10px 15px",
          paddingBottom: "5px",
        }}
      >
        {SecondaryDescription}
      </p>

      <div
        className="actions"
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          backgroundColor: "white",
          padding: "15px",
          marginBottom: "10px",
          // marginTop: "10px",
          paddingBottom: "10px",
        }}
      >
        {AllowDelete && (
          <>
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                // backgroundColor: "white",
              }}
            >
              <button
                type="button"
                style={{
                  padding: "8px 14px",
                  border: "1.25px solid maroon",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setConfirmConfiguration({
                    caption: "Delete Selected lines",
                    message: "Are you sure you want to delete selected lines?",
                    onConfirm: DeleteSelectedLines,
                  });
                  setConfirmedOpen(true);
                }}
              >
                Delete Selected Lines
              </button>
              <button
                type="button"
                style={{
                  padding: "8px 14px",
                  border: "1.25px solid maroon",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setConfirmConfiguration({
                    caption: "Delete Selected lines",
                    message: "Are you sure you want to delete the all lines?",
                    onConfirm: DeleteAllLines,
                  });
                  setConfirmedOpen(true);
                }}
              >
                Delete All Lines
              </button>
            </div>
          </>
        )}
        {actions.map((action, i) => (
          <button
            key={i}
            type="button"
            onClick={() => {
              if (typeof action.fx === "function") {
                // Dynamic selection based on the processing needs
                action.fx({
                  selectedRows: selectedRowsData, // All selected rows
                  firstSelectedRow: firstSelectedRow, // First selected row
                  selectedRowsIds: selectedRows, // Selected row IDs
                });
              }
            }}
            style={{
              padding: "8px 14px",
              border: "1.25px solid maroon",
              borderRadius: "4px",
              cursor: "pointer",
              display: action.visible === false ? "none" : "inline-flex",
              backgroundColor: action.color || "none",
            }}
          >
            {action.icon && (
              <span style={{ marginRight: "6px" }}>{action.icon}</span>
            )}
            {action.name}
          </button>
        ))}
      </div>
      {/* )} */}
      {ErrorMsg && (
        <div
          style={{
            padding: "15px",
            marginBottom: "10px",
            marginTop: "10px",
            paddingBottom: "10px",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            backgroundColor: "white",
            color: "red",
          }}
        >
          {ErrorMsg}
        </div>
      )}
      <DataTable
        className="ModifiedDatatable"
        columns={finalColumns}
        data={fetchedData}
        customStyles={customStyles}
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 300px)"
        selectableRows
        responsive
        onSelectedRowsChange={handleSelectedRowsChange}
        noDataComponent={
          <div className="p-4 text-center text-gray-500">No Data found</div>
        }
        progressPending={isLoading}
      />
      <ConfirmationDialog
        isOpen={confirmedOpen}
        toggle={() => setConfirmedOpen(!confirmedOpen)}
        caption={confirmConfiguration.caption}
        message={confirmConfiguration.message}
        onConfirm={() => {
          confirmConfiguration.onConfirm();
          setConfirmedOpen(false);
        }}
      />

      <SuccessMessage
        isOpen={issuccedOpen}
        toggle={() => setSucceededOpen(!issuccedOpen)}
        caption="Success"
        message={popupMsg}
      />

      <ErrorMessage
        isOpen={isErrOpen}
        toggle={() => setErrorOpen(!isErrOpen)}
        caption="Error"
        message={ErrorMsg}
      />
    </div>
  );
};
export default EditableDatable;
